/**
 * Payout country helpers.
 *
 * Stripe Connect countries stay in configStripe.supportedCountries.
 * The dropdown also includes countries where providers can receive via
 * Wise, Revolut, or PayPal (union of those providers' receive coverage).
 */

import { supportedCountries as stripeSupportedCountries } from '../config/configStripe';

const toCountryCode = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value.trim().toUpperCase() || null;
  }
  const code = value.code || value.country || value.countryCode;
  return typeof code === 'string' ? code.trim().toUpperCase() || null : null;
};

/** @type {string[]} ISO 3166-1 alpha-2 codes for Stripe Connect (current app list). */
export const getStripeCountryCodes = (stripeCountries = stripeSupportedCountries) =>
  (stripeCountries || []).map(toCountryCode).filter(Boolean);

/**
 * PayPal Payouts receive/withdraw countries (ISO codes from PayPal supported-features),
 * plus Revolut personal-account countries and common Wise personal-account markets
 * not already covered by Stripe.
 *
 * Sources:
 * - https://developer.paypal.com/payouts/supported-features
 * - Revolut personal account eligibility (EEA, UK, US, AU, NZ, SG, JP, BR, CH, …)
 * - Wise personal account / receive markets overlapping those lists
 */
export const ALTERNATIVE_PROVIDER_COUNTRY_CODES = [
  'AD',
  'AE',
  'AL',
  'AM',
  'AR',
  'AZ',
  'BA',
  'BD',
  'BH',
  'BM',
  'BR',
  'BS',
  'BW',
  'BY',
  'CL',
  'CO',
  'CR',
  'DO',
  'EC',
  'EG',
  'FO',
  'GE',
  'GH',
  'GI',
  'GL',
  'GT',
  'HN',
  'HR',
  'HU',
  'ID',
  'IL',
  'IN',
  'IS',
  'JM',
  'JO',
  'KE',
  'KH',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LI',
  'LK',
  'LS',
  'MA',
  'MC',
  'MD',
  'ME',
  'MK',
  'MU',
  'MY',
  'MZ',
  'NG',
  'NI',
  'NP',
  'OM',
  'PA',
  'PE',
  'PH',
  'PK',
  'QA',
  'RE',
  'RS',
  'SA',
  'SM',
  'SN',
  'SV',
  'TH',
  'TR',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UY',
  'VE',
  'VN',
  'ZA',
];

/**
 * Union of Stripe Connect countries and Wise/Revolut/PayPal-supported countries.
 * @param {Array<{ code: string }>} [stripeCountries]
 * @returns {string[]} sorted unique ISO codes
 */
export const getAllPayoutCountryCodes = (stripeCountries = stripeSupportedCountries) => {
  const stripeCodes = getStripeCountryCodes(stripeCountries);
  const merged = new Set([...stripeCodes, ...ALTERNATIVE_PROVIDER_COUNTRY_CODES]);
  return Array.from(merged).sort();
};

/**
 * @param {string} countryCode
 * @param {Array<{ code: string }>} [stripeCountries]
 * @returns {boolean}
 */
export const isStripePayoutCountry = (countryCode, stripeCountries = stripeSupportedCountries) => {
  const code = toCountryCode(countryCode);
  if (!code) {
    return false;
  }
  return getStripeCountryCodes(stripeCountries).includes(code);
};

/**
 * Localized country label. Prefers existing translation keys, falls back to Intl.DisplayNames.
 * @param {object} intl - react-intl intl shape
 * @param {string} countryCode
 * @returns {string}
 */
export const getPayoutCountryLabel = (intl, countryCode) => {
  if (!countryCode) {
    return '';
  }
  const key = `StripeConnectAccountForm.countryNames.${countryCode}`;
  if (intl?.messages?.[key]) {
    return intl.messages[key];
  }
  try {
    const locale = intl?.locale || 'en';
    return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) || countryCode;
  } catch (e) {
    return countryCode;
  }
};

/**
 * Country codes sorted by localized display name.
 * @param {object} intl
 * @param {string[]} [countryCodes]
 * @returns {string[]}
 */
export const getSortedPayoutCountryCodes = (intl, countryCodes) => {
  const codes = countryCodes || getAllPayoutCountryCodes();
  return [...codes].sort((a, b) =>
    getPayoutCountryLabel(intl, a).localeCompare(
      getPayoutCountryLabel(intl, b),
      intl?.locale || 'en'
    )
  );
};

/**
 * Operator override on the payout page. Append `?stripe=1` to
 * `/account/payments` (or the listing payout modal URL) to open a separate
 * Stripe Connect flow. That flow does not read or change the user's saved
 * payout configuration, and the normal page keeps showing the user's setup.
 * Not linked from the normal UI.
 */
export const OPERATOR_STRIPE_QUERY_PARAM = 'stripe';

export const hasOperatorStripeOverride = search => {
  if (typeof search !== 'string' || !search) {
    return false;
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  if (!params.has(OPERATOR_STRIPE_QUERY_PARAM)) {
    return false;
  }
  const value = params.get(OPERATOR_STRIPE_QUERY_PARAM);
  if (value == null || value === '') {
    return true;
  }
  const normalized = String(value).toLowerCase();
  return normalized === '1' || normalized === 'true';
};

/**
 * Currencies commonly used for bank-transfer payouts. Saved values that are
 * not in this list are still offered so existing accounts remain selectable.
 */
export const BANK_PAYOUT_CURRENCY_CODES = [
  'AED',
  'ARS',
  'AUD',
  'BDT',
  'BGN',
  'BHD',
  'BRL',
  'CAD',
  'CHF',
  'CLP',
  'CNY',
  'COP',
  'CZK',
  'DKK',
  'EGP',
  'EUR',
  'GBP',
  'GHS',
  'HKD',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'JPY',
  'KES',
  'KRW',
  'KWD',
  'LKR',
  'MAD',
  'MXN',
  'MYR',
  'NGN',
  'NOK',
  'NZD',
  'OMR',
  'PEN',
  'PHP',
  'PKR',
  'PLN',
  'QAR',
  'RON',
  'SAR',
  'SEK',
  'SGD',
  'THB',
  'TRY',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'UYU',
  'VND',
  'ZAR',
];

/**
 * Dropdown options for bank-account currency. Labels use ISO code + localized name.
 * @param {object} intl
 * @param {string[]} [extraCodes]
 * @returns {Array<{ value: string, label: string }>}
 */
export const getBankPayoutCurrencyOptions = (intl, extraCodes = []) => {
  const extras = (extraCodes || [])
    .map(code => (typeof code === 'string' ? code.trim() : ''))
    .filter(Boolean);
  const codes = Array.from(new Set([...BANK_PAYOUT_CURRENCY_CODES, ...extras]));
  const locale = intl?.locale || 'en';
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'currency' });
  } catch (e) {
    displayNames = null;
  }
  return codes
    .map(code => {
      const name = displayNames ? displayNames.of(code) : null;
      const label = name && name !== code ? `${code} · ${name}` : code;
      return { value: code, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label, locale));
};
